import { useMutation, useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import styled from 'styled-components';

import { DocumentRecordStatus, DueDiligencePageListUsersQuery, Role, StrategyDetailsPageListDocumentRecordsQuery } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { createSimpleSelectOption } from '@/components/basicComponents/select/utils';
import { LIST_USERS_QUERY } from '@/components/dueDiligence-page/queries';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { themeVar } from '@/styles/theme';
import { STRATEGY_DETAILS_PAGE_UPDATE_DOCUMENT_RECORD_MUTATION } from '../queries';

interface ChangeAssigneeProps {
  isOpen: boolean;
  currentRow: StrategyDetailsPageListDocumentRecordsQuery['listDocumentRecords']['records'][number] | null;
  listDocumentRecordsRefetch: () => void;
  onClose: () => void;
}

const ChangeOwner = ({ isOpen, currentRow, listDocumentRecordsRefetch, onClose }: ChangeAssigneeProps) => {
  const { user } = useAuth();
  const { default: defaultErrorMessage } = useErrorMessages();

  const [selectedOwner, setSelectedOwner] = useState('');
  const [usersList, setUsersList] = useState<ISelectValue[]>([]);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!currentRow) return;
    const { owner } = currentRow;
    setSelectedOwner(owner ? `${owner?.firstName} ${owner?.lastName}` : 'Select');
  }, [currentRow]);

  const [updateDocumentRecord, { loading: updateDocumentRecordLoading }] = useMutation(STRATEGY_DETAILS_PAGE_UPDATE_DOCUMENT_RECORD_MUTATION, {
    onError: (error) => {
      console.error(error);
      setErrorMessage(defaultErrorMessage);
    }
  });

  const { data: listUsersData, loading: listUsersLoading } = useQuery(LIST_USERS_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'cache-and-network',
    variables: {
      filters: {
        roles: [Role.InvestmentLead, Role.InvestmentMember],
        tenantId: user.tenant.id
      }
    },
    onCompleted: (data) => {
      if (!data?.listUsers?.length) return;
      setUsersList(
        data.listUsers.map((user: DueDiligencePageListUsersQuery['listUsers'][number]) =>
          createSimpleSelectOption(`${user.firstName} ${user.lastName}`)
        )
      );
    },
    onError: (error) => {
      console.error(error);
    }
  });

  const changeOwner = () => {
    setErrorMessage(null);
    const selectedOwnerObject = listUsersData?.listUsers?.find(
      (item: DueDiligencePageListUsersQuery['listUsers'][number]) => `${item.firstName} ${item.lastName}` === selectedOwner
    );

    if (!selectedOwnerObject) {
      setErrorMessage(defaultErrorMessage);
      return;
    }

    updateDocumentRecord({
      variables: {
        data: {
          id: currentRow.id,
          ownerEmail: selectedOwnerObject.email,
          status: currentRow?.status as DocumentRecordStatus
        }
      },
      onCompleted: () => {
        listDocumentRecordsRefetch();
        onClose();
      }
    });
  };

  if (!currentRow) return <></>;

  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      title="Change Owner"
      confirmButton={{ name: 'Save', onClick: changeOwner, disabled: listUsersLoading || updateDocumentRecordLoading || selectedOwner === 'Select' }}
      showRequiredFields={!updateDocumentRecordLoading}
      showCloseIcon={!updateDocumentRecordLoading}
      showCancelButton={!updateDocumentRecordLoading}
      isUseOutsideClick={!updateDocumentRecordLoading}
    >
      {updateDocumentRecordLoading ? (
        <ModalLoader />
      ) : (
        <>
          {errorMessage && (
            <Banner
              icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
              title="Error"
              description={errorMessage}
              bgColor={themeVar('context.error')}
            />
          )}
          <OwnerSelectWrapper>
            <Select
              value={selectedOwner}
              setValue={setSelectedOwner}
              options={usersList}
              label="Select An Owner"
              loading={listUsersLoading}
              isRequiredField
            />
          </OwnerSelectWrapper>
        </>
      )}
    </ModalWrappedContent>
  );
};

export default ChangeOwner;

const OwnerSelectWrapper = styled.div`
  margin-top: 20px;
`;
